/**
 * API Service for Learning Rules Management
 */

import { supabase } from '@/integrations/supabase/client';
import type {
  LearnedRule,
  RuleStatistics,
  CreateRuleRequest,
  UpdateRuleRequest,
  RuleFilter,
  ManualCorrection,
  RuleApplicationHistory,
} from '@/types/learningRules';

export class LearningRulesService {
  /**
   * Get hardcoded rules from the Edge Function as read-only reference
   */
  private static async getHardcodedRules(): Promise<LearnedRule[]> {
    const hardcodedRules: Partial<LearnedRule>[] = [
      {
        pattern_key: 'playstation network',
        pattern_type: 'counterparty',
        suggested_debit_account: '2260',
        suggested_credit_account: '1020',
        description_template: 'Privatentnahme PlayStation',
        vat_code: '0',
        current_confidence: 0.95,
        is_active: true,
        scope: 'global',
      },
      {
        pattern_key: 'skribble',
        pattern_type: 'counterparty',
        suggested_debit_account: '6700',
        suggested_credit_account: '1020',
        description_template: 'Informatikaufwand Skribble',
        vat_code: '8.1',
        current_confidence: 0.92,
        is_active: true,
        scope: 'global',
      },
      {
        pattern_key: 'agrola',
        pattern_type: 'counterparty',
        suggested_debit_account: '6710',
        suggested_credit_account: '1020',
        description_template: 'Fahrzeugaufwand Treibstoff',
        vat_code: '8.1',
        current_confidence: 0.95,
        is_active: true,
        scope: 'global',
      },
      {
        pattern_key: 'netflix',
        pattern_type: 'counterparty',
        suggested_debit_account: '2260',
        suggested_credit_account: '1020',
        description_template: 'Privatentnahme Netflix',
        vat_code: '0',
        current_confidence: 0.95,
        is_active: true,
        scope: 'global',
      },
      {
        pattern_key: 'spotify',
        pattern_type: 'counterparty',
        suggested_debit_account: '2260',
        suggested_credit_account: '1020',
        description_template: 'Privatentnahme Spotify',
        vat_code: '0',
        current_confidence: 0.95,
        is_active: true,
        scope: 'global',
      },
      {
        pattern_key: 'mcdonalds',
        pattern_type: 'counterparty',
        suggested_debit_account: '6610',
        suggested_credit_account: '1020',
        description_template: 'Werbeaufwand Print',
        vat_code: '8.1',
        current_confidence: 0.85,
        is_active: true,
        scope: 'global',
      },
      {
        pattern_key: 'google ads',
        pattern_type: 'counterparty',
        suggested_debit_account: '6600',
        suggested_credit_account: '1020',
        description_template: 'Werbeaufwand Digital Google',
        vat_code: '8.1',
        current_confidence: 0.95,
        is_active: true,
        scope: 'global',
      },
      {
        pattern_key: 'facebook',
        pattern_type: 'counterparty',
        suggested_debit_account: '6600',
        suggested_credit_account: '1020',
        description_template: 'Werbeaufwand Digital Facebook',
        vat_code: '8.1',
        current_confidence: 0.95,
        is_active: true,
        scope: 'global',
      },
    ];

    return hardcodedRules.map((rule, index) => ({
      id: `hardcoded-${index}`,
      created_at: '2025-01-01T00:00:00Z',
      updated_at: '2025-01-01T00:00:00Z',
      user_id: null,
      organization_id: null,
      pattern_type: rule.pattern_type as 'counterparty' | 'description' | 'amount_range',
      pattern_key: rule.pattern_key!,
      pattern_value: null,
      suggested_debit_account: rule.suggested_debit_account!,
      suggested_credit_account: rule.suggested_credit_account!,
      vat_code: rule.vat_code || null,
      vat_account: rule.vat_account || null,
      description_template: rule.description_template || null,
      is_active: rule.is_active ?? true,
      is_deprecated: false,
      deprecated_at: null,
      deprecated_reason: null,
      scope: rule.scope as 'user' | 'organization' | 'global',
      base_confidence: rule.current_confidence || 0.8,
      current_confidence: rule.current_confidence || 0.8,
      usage_count: 0,
      success_count: 0,
      correction_count: 0,
      is_shared: true,
      learned_from_correction_id: null,
      learned_from_timestamp: null,
      semantic_threshold: 0.85,
      embedding_id: null,
      metadata: {},
    })) as LearnedRule[];
  }

  /**
   * Get all learned rules with filtering
   */
  static async getRules(filters: RuleFilter = {}): Promise<LearnedRule[]> {
    let query = supabase
      .from('learned_rules')
      .select('*')
      .order('current_confidence', { ascending: false });

    // Note: learned_rules uses user_id, not mandate_id
    // Filter by current user (user_id will be automatically applied via RLS)

    if (filters.status === 'active') {
      query = query.eq('is_active', true);
    } else if (filters.status === 'inactive') {
      query = query.eq('is_active', false);
    } else if (filters.status === 'needs_review') {
      query = query
        .eq('is_active', true)
        .lt('current_confidence', 0.75);
    } else if (filters.status === 'shared') {
      query = query.eq('is_shared', true);
    }

    if (filters.pattern_type) {
      query = query.eq('pattern_type', filters.pattern_type);
    }

    if (filters.search) {
      query = query.ilike('pattern_key', `%${filters.search}%`);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching rules:', error);
      throw error;
    }

    const userRules = (data || []) as LearnedRule[];
    
    // Include hardcoded rules for reference (except when filtering for specific status)
    if (!filters.status || filters.status === 'all' || filters.status === 'shared') {
      const hardcodedRules = await this.getHardcodedRules();
      return [...hardcodedRules, ...userRules];
    }

    return userRules;
  }

  /**
   * Get statistics for learned rules (user-level)
   */
  static async getStatistics(userId?: string): Promise<RuleStatistics> {
    // learning_statistics is a view that uses user_id, not mandate_id
    let query = supabase
      .from('learning_statistics')
      .select('*');
    
    if (userId) {
      query = query.eq('user_id', userId);
    }
    
    const { data, error } = await query.maybeSingle();

    if (error) {
      console.error('Error fetching statistics:', error);
      throw error;
    }

    if (!data) {
      return {
        total_rules: 0,
        active_rules: 0,
        avg_confidence: 0,
        avg_success_rate: 0,
        needs_review_count: 0,
      };
    }

    // Include hardcoded rules in statistics
    const hardcodedRules = await this.getHardcodedRules();
    const hardcodedActiveCount = hardcodedRules.filter(r => r.is_active).length;

    return {
      total_rules: (data.total_learned_rules || 0) + hardcodedRules.length,
      active_rules: (data.active_rules || 0) + hardcodedActiveCount,
      avg_confidence: data.avg_rule_confidence || 0,
      avg_success_rate: data.success_rate || 0,
      needs_review_count: await this.getNeedsReviewCount(userId),
    };
  }

  /**
   * Get count of rules that need review (for current user via RLS)
   */
  private static async getNeedsReviewCount(userId?: string): Promise<number> {
    const query = supabase
      .from('learned_rules')
      .select('id', { count: 'exact', head: true })
      .eq('is_active', true)
      .lt('current_confidence', 0.75);

    // RLS will automatically filter by user_id

    const { count, error } = await query;

    if (error) {
      console.error('Error counting rules needing review:', error);
      return 0;
    }

    return count || 0;
  }

  /**
   * Get single rule by ID
   */
  static async getRule(ruleId: string): Promise<LearnedRule | null> {
    const { data, error } = await supabase
      .from('learned_rules')
      .select('*')
      .eq('id', ruleId)
      .single();

    if (error) {
      console.error('Error fetching rule:', error);
      throw error;
    }

    return data as LearnedRule | null;
  }

  /**
   * Create new learned rule
   */
  static async createRule(rule: CreateRuleRequest): Promise<LearnedRule> {
    const { data, error } = await supabase
      .from('learned_rules')
      .insert([{
        ...rule,
        current_confidence: rule.base_confidence || 0.80,
        usage_count: 0,
        success_count: 0,
        correction_count: 0,
        is_active: true,
        is_deprecated: false,
      }])
      .select()
      .single();

    if (error) {
      console.error('Error creating rule:', error);
      throw error;
    }

    return data as LearnedRule;
  }

  /**
   * Update existing rule
   */
  static async updateRule(ruleId: string, updates: UpdateRuleRequest): Promise<LearnedRule> {
    const { data, error } = await supabase
      .from('learned_rules')
      .update(updates)
      .eq('id', ruleId)
      .select()
      .single();

    if (error) {
      console.error('Error updating rule:', error);
      throw error;
    }

    return data as LearnedRule;
  }

  /**
   * Delete (soft delete) a rule
   */
  static async deleteRule(ruleId: string): Promise<void> {
    const { error } = await supabase
      .from('learned_rules')
      .update({
        is_active: false,
        is_deprecated: true,
        deprecated_reason: 'Deleted by user',
        deprecated_at: new Date().toISOString(),
      })
      .eq('id', ruleId);

    if (error) {
      console.error('Error deleting rule:', error);
      throw error;
    }
  }

  /**
   * Toggle rule active status
   */
  static async toggleRuleStatus(ruleId: string, isActive: boolean): Promise<LearnedRule> {
    return this.updateRule(ruleId, { is_active: isActive });
  }

  /**
   * Copy rule (creates a duplicate for current user)
   */
  static async copyRule(
    ruleId: string,
    options: {
      resetConfidence?: boolean;
      markAsShared?: boolean;
    } = {}
  ): Promise<LearnedRule> {
    // Get source rule
    const sourceRule = await this.getRule(ruleId);
    if (!sourceRule) {
      throw new Error('Source rule not found');
    }

    // Create new rule for current user (user_id will be set automatically)
    const newRule: CreateRuleRequest = {
      pattern_type: sourceRule.pattern_type,
      pattern_key: sourceRule.pattern_key,
      pattern_value: sourceRule.pattern_value,
      suggested_debit_account: sourceRule.suggested_debit_account,
      suggested_credit_account: sourceRule.suggested_credit_account,
      vat_code: sourceRule.vat_code,
      vat_account: sourceRule.vat_account,
      description_template: sourceRule.description_template,
      base_confidence: options.resetConfidence ? 0.70 : sourceRule.current_confidence,
      is_shared: options.markAsShared || false,
      scope: options.markAsShared ? 'organization' : 'user',
    };

    return this.createRule(newRule);
  }

  /**
   * Share rule with organization
   */
  static async shareRule(ruleId: string): Promise<LearnedRule> {
    return this.updateRule(ruleId, {
      is_shared: true,
      scope: 'organization',
    });
  }

  /**
   * Get rule application history
   */
  static async getRuleApplicationHistory(ruleId: string): Promise<RuleApplicationHistory[]> {
    const { data, error } = await supabase
      .from('rule_application_history')
      .select('*')
      .eq('rule_id', ruleId)
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) {
      console.error('Error fetching rule history:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Get corrections for current user (via RLS)
   */
  static async getCorrections(userId?: string, limit: number = 50): Promise<ManualCorrection[]> {
    const query = supabase
      .from('manual_corrections')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    // RLS will automatically filter by user_id

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching corrections:', error);
      throw error;
    }

    return (data || []) as ManualCorrection[];
  }

  /**
   * Get all rules for current user (grouped by pattern)
   */
  static async getRulesForComparison(): Promise<Map<string, LearnedRule[]>> {
    const { data, error } = await supabase
      .from('learned_rules')
      .select('*')
      .eq('is_active', true);

    if (error) {
      console.error('Error fetching rules for comparison:', error);
      throw error;
    }

    // Group by pattern_key
    const grouped = new Map<string, LearnedRule[]>();

    (data || []).forEach((rule) => {
      const typedRule = rule as LearnedRule;
      if (!grouped.has(typedRule.pattern_key)) {
        grouped.set(typedRule.pattern_key, []);
      }
      grouped.get(typedRule.pattern_key)!.push(typedRule);
    });

    return grouped;
  }

  /**
   * Bulk operations
   */
  static async bulkToggleStatus(ruleIds: string[], isActive: boolean): Promise<void> {
    const { error } = await supabase
      .from('learned_rules')
      .update({ is_active: isActive })
      .in('id', ruleIds);

    if (error) {
      console.error('Error in bulk toggle:', error);
      throw error;
    }
  }

  static async bulkDelete(ruleIds: string[]): Promise<void> {
    const { error } = await supabase
      .from('learned_rules')
      .update({
        is_active: false,
        is_deprecated: true,
        deprecated_reason: 'Bulk deleted by user',
        deprecated_at: new Date().toISOString(),
      })
      .in('id', ruleIds);

    if (error) {
      console.error('Error in bulk delete:', error);
      throw error;
    }
  }
}

export default LearningRulesService;
